import { supabase } from '../../supabaseClient';
import { tt } from '../i18n';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const REQUEST_TIMEOUT = 15_000;

export type ApiErrorKind = 'network' | 'auth' | 'validation' | 'server';

export class ApiError extends Error {
  kind: ApiErrorKind;
  status?: number;
  body?: Record<string, unknown>;

  constructor(
    kind: ApiErrorKind,
    message: string,
    status?: number,
    body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.body = body;
  }

  get isRetryable(): boolean {
    return this.kind === 'network' || this.kind === 'server';
  }

  /**
   * 070a — stable machine-readable code (the backend's validation.*
   * catalog key). Match on this, never on the translated `message`,
   * which renders in the caller's locale.
   */
  get code(): string | undefined {
    const c = this.body?.code;
    return typeof c === 'string' ? c : undefined;
  }

  get userMessage(): string {
    switch (this.kind) {
      case 'network':
        return tt('errors.api.network_error');
      case 'auth':
        return tt('errors.api.session_expired');
      case 'validation':
        // Backend renders validation messages in the request locale
        // (see LocaleService.resolveRequestLocale). Pass through.
        return this.message;
      case 'server':
        return tt('errors.api.server_error');
    }
  }
}

let onAuthExpired: (() => void) | null = null;
export function setAuthExpiredHandler(handler: () => void) {
  onAuthExpired = handler;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError('auth', 'Not authenticated', 401);
  return { Authorization: `Bearer ${token}` };
}

function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number = REQUEST_TIMEOUT,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timeout),
  );
}

function classifyAndThrow(
  status: number,
  message: string,
  body?: Record<string, unknown>,
): never {
  if (status === 401) {
    // Unauthenticated — the session is missing/expired. Trigger the
    // session-expired banner + re-login prompt.
    if (onAuthExpired) onAuthExpired();
    throw new ApiError('auth', message, status, body);
  }
  if (status === 403) {
    // Forbidden ≠ expired session. The user is authenticated but lacks
    // permission (e.g. a co-director hitting an owner-only endpoint).
    // Do NOT show the session-expired banner; surface the backend's
    // message via the validation kind (pass-through).
    throw new ApiError('validation', message, status, body);
  }
  if (status >= 400 && status < 500) {
    throw new ApiError('validation', message, status, body);
  }
  throw new ApiError('server', message, status, body);
}

type FetchOptions = RequestInit & { auth?: boolean; timeoutMs?: number };

async function baseFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { auth = true, timeoutMs, headers: customHeaders, ...rest } = options;
  const authHeaders: Record<string, string> = auth ? await getAuthHeaders() : {};
  // Nest needs Content-Type to parse JSON bodies. Skip it for FormData
  // so the browser can set the multipart boundary itself.
  if (rest.body !== undefined && !(rest.body instanceof FormData)) {
    authHeaders['Content-Type'] = 'application/json';
  }
  // Tell the shared backend which tenant this XHR is for. Story 061 —
  // cross-origin XHR can't carry the tenant via Host (that's the API's
  // own host).
  const tenantHeaders: Record<string, string> =
    typeof window !== 'undefined'
      ? { 'X-Swiss-Tenant-Host': window.location.hostname }
      : {};

  let res: Response;
  try {
    res = await fetchWithTimeout(
      `${API_URL}${path}`,
      {
        ...rest,
        headers: {
          ...tenantHeaders,
          ...authHeaders,
          ...(customHeaders as Record<string, string> | undefined),
        },
      },
      timeoutMs,
    );
  } catch (err) {
    throw new ApiError('network', (err as Error).message);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    classifyAndThrow(res.status, body.message || `API error: ${res.status}`, body);
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return res.json();
}

export function get<T>(path: string): Promise<T> {
  return baseFetch<T>(path);
}

export function post<T>(
  path: string,
  body?: unknown,
  opts?: { auth?: boolean; timeoutMs?: number },
): Promise<T> {
  return baseFetch<T>(path, {
    method: 'POST',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...opts,
  });
}

export function postFormData<T>(
  path: string,
  form: FormData,
  opts?: { timeoutMs?: number; auth?: boolean },
): Promise<T> {
  return baseFetch<T>(path, { method: 'POST', body: form, ...opts });
}

export function patch<T>(path: string, body: unknown): Promise<T> {
  return baseFetch<T>(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function put<T>(path: string, body: unknown): Promise<T> {
  return baseFetch<T>(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function del<T>(path: string, body?: unknown): Promise<T> {
  return baseFetch<T>(path, {
    method: 'DELETE',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

export function publicGet<T>(path: string): Promise<T> {
  return baseFetch<T>(path, { auth: false });
}

export function publicPost<T>(path: string, body?: unknown): Promise<T> {
  return baseFetch<T>(path, {
    method: 'POST',
    auth: false,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}
